import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';

interface Survey {
  id: string;
  title: string;
  description: string;
  questions: SurveyQuestion[];
  is_active: boolean;
  created_at: string;
}

interface SurveyQuestion {
  id: string;
  text: string;
  type: 'multiple_choice' | 'text' | 'rating' | 'yes_no';
  options?: string[];
  required: boolean;
}

interface SurveyResponse {
  id: string;
  survey_id: string;
  created_at: string;
}

interface SurveysPanelProps {
  customerEmail: string;
  onClose: () => void;
}

export default function SurveysPanel({ customerEmail, onClose }: SurveysPanelProps) {
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [responses, setResponses] = useState<SurveyResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSurvey, setSelectedSurvey] = useState<Survey | null>(null);
  const [answers, setAnswers] = useState<Record<string, string | number>>({});
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    fetchSurveys();
    fetchResponses();
  }, [customerEmail]);

  const fetchSurveys = async () => {
    try {
      const { data, error } = await supabase
        .from('surveys')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      if (data) setSurveys(data);
    } catch (error) {
      console.error('Anketler yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchResponses = async () => {
    try {
      const { data, error } = await supabase
        .from('survey_responses')
        .select('id, survey_id, created_at')
        .eq('respondent_email', customerEmail);

      if (error) throw error;
      if (data) setResponses(data);
    } catch (error) {
      console.error('Yanıtlar yüklenirken hata:', error);
    }
  };

  const hasResponded = (surveyId: string) => {
    return responses.some(r => r.survey_id === surveyId);
  };

  const handleAnswerChange = (questionId: string, value: string | number) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleSubmit = async () => {
    if (!selectedSurvey) return;

    // Zorunlu soruları kontrol et
    const requiredQuestions = selectedSurvey.questions.filter(q => q.required);
    const missingAnswers = requiredQuestions.filter(q => !answers[q.id]);

    if (missingAnswers.length > 0) {
      alert('Lütfen tüm zorunlu soruları yanıtlayın');
      return;
    }

    setSubmitting(true);
    try {
      const { error } = await supabase
        .from('survey_responses')
        .insert({
          survey_id: selectedSurvey.id,
          respondent_email: customerEmail,
          respondent_name: customerEmail.split('@')[0],
          answers: answers,
          respondent_type: 'customer'
        });

      if (error) throw error;

      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        setSelectedSurvey(null);
        setAnswers({});
        fetchResponses();
      }, 2000);
    } catch (error) {
      console.error('Anket gönderilirken hata:', error);
      alert('Anket gönderilirken bir hata oluştu');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  if (selectedSurvey) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full flex flex-col" style={{ maxHeight: '90vh' }}>
          
          {/* Sticky Header */}
          <div className="bg-white border-b border-gray-200 p-5 rounded-t-xl flex-shrink-0">
            <div className="flex items-center justify-between">
              <h2 className="text-gray-900 font-bold text-xl">{selectedSurvey.title}</h2>
              <button
                onClick={() => {
                  setSelectedSurvey(null);
                  setAnswers({});
                }}
                className="w-9 h-9 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer flex-shrink-0 ml-4"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            {selectedSurvey.description && (
              <p className="text-gray-500 text-sm bg-gray-50 rounded-lg p-3 border border-gray-100">{selectedSurvey.description}</p>
            )}

            {selectedSurvey.questions.map((question, index) => (
              <div key={question.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <label className="block text-gray-900 font-medium mb-3 text-sm">
                  {index + 1}. {question.text}
                  {question.required && <span className="text-red-500 ml-1">*</span>}
                </label>

                {question.type === 'multiple_choice' && question.options && (
                  <div className="space-y-2">
                    {question.options.map((option, optIndex) => (
                      <label
                        key={optIndex}
                        className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <input
                          type="radio"
                          name={question.id}
                          value={option}
                          checked={answers[question.id] === option}
                          onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                          className="w-4 h-4 cursor-pointer"
                        />
                        <span className="text-gray-700 text-sm">{option}</span>
                      </label>
                    ))}
                  </div>
                )}

                {question.type === 'text' && (
                  <textarea
                    value={answers[question.id] as string || ''}
                    onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                    rows={3}
                    placeholder="Yanıtınızı buraya yazın..."
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none resize-none"
                  />
                )}

                {question.type === 'rating' && (
                  <div className="flex items-center gap-2 py-1">
                    {[1, 2, 3, 4, 5].map(star => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => handleAnswerChange(question.id, star)}
                        className="p-1 cursor-pointer transition-all hover:scale-125"
                      >
                        <i className={`text-3xl ${
                          star <= ((answers[question.id] as number) || 0)
                            ? 'ri-star-fill text-amber-400'
                            : 'ri-star-line text-gray-300'
                        }`}></i>
                      </button>
                    ))}
                    {answers[question.id] && (
                      <span className="ml-2 text-gray-600 text-sm font-medium">
                        {answers[question.id]}/5
                      </span>
                    )}
                  </div>
                )}

                {question.type === 'yes_no' && (
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => handleAnswerChange(question.id, 'Evet')}
                      className={`flex-1 px-4 py-2 rounded-lg font-medium text-sm transition-all cursor-pointer ${
                        answers[question.id] === 'Evet'
                          ? 'bg-green-500 text-white'
                          : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Evet
                    </button>
                    <button
                      type="button"
                      onClick={() => handleAnswerChange(question.id, 'Hayır')}
                      className={`flex-1 px-4 py-2 rounded-lg font-medium text-sm transition-all cursor-pointer ${
                        answers[question.id] === 'Hayır'
                          ? 'bg-red-500 text-white'
                          : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Hayır
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Sticky Footer */}
          <div className="bg-white border-t border-gray-200 p-4 rounded-b-xl flex-shrink-0">
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setSelectedSurvey(null);
                  setAnswers({});
                }}
                disabled={submitting}
                className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors cursor-pointer disabled:opacity-50 whitespace-nowrap text-sm"
              >
                İptal
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white font-medium rounded-lg transition-all cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2 whitespace-nowrap text-sm"
              >
                {submitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Gönderiliyor...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line"></i>
                    Anketi Gönder
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {showSuccess && (
          <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-8 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-checkbox-circle-line text-4xl text-green-600"></i>
              </div>
              <h3 className="text-gray-900 font-bold text-2xl mb-2">Teşekkür Ederiz!</h3>
              <p className="text-gray-600 text-sm">Anket yanıtınız başarıyla kaydedildi</p>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-gray-900 font-bold text-2xl mb-1 flex items-center gap-2">
                <i className="ri-survey-line text-purple-500"></i>
                Anketler
              </h2>
              <p className="text-gray-600 text-sm">Görüşleriniz bizim için çok değerli</p>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <i className="ri-loader-4-line text-4xl text-purple-500 animate-spin"></i>
            </div>
          ) : surveys.length === 0 ? (
            <div className="text-center py-12">
              <i className="ri-survey-line text-6xl text-gray-300 mb-4"></i>
              <p className="text-gray-500 text-lg mb-2">Henüz aktif anket yok</p>
              <p className="text-gray-400 text-sm">Yeni anketler eklendiğinde burada görünecek</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {surveys.map((survey) => {
                const responded = hasResponded(survey.id);
                return (
                  <div
                    key={survey.id}
                    className={`bg-white border-2 rounded-xl p-5 transition-all ${
                      responded
                        ? 'border-green-200 bg-green-50'
                        : 'border-purple-200 hover:border-purple-300 hover:shadow-md'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="text-gray-900 font-semibold text-lg mb-1">{survey.title}</h3>
                        {survey.description && (
                          <p className="text-gray-600 text-sm line-clamp-2">{survey.description}</p>
                        )}
                      </div>
                      {responded && (
                        <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 ml-3">
                          <i className="ri-checkbox-circle-line text-white text-lg"></i>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                      <span className="flex items-center gap-1">
                        <i className="ri-question-line"></i>
                        {survey.questions.length} soru
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line"></i>
                        {formatDate(survey.created_at)}
                      </span>
                    </div>

                    {responded ? (
                      <div className="bg-green-100 border border-green-200 rounded-lg p-3 text-center">
                        <p className="text-green-700 text-sm font-medium">
                          <i className="ri-checkbox-circle-line mr-1"></i>
                          Bu anketi doldurdunuz
                        </p>
                      </div>
                    ) : (
                      <button
                        onClick={() => setSelectedSurvey(survey)}
                        className="w-full px-4 py-3 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white font-medium rounded-lg transition-all cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap"
                      >
                        <i className="ri-edit-box-line"></i>
                        Anketi Doldur
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
